'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createEnterApiTransport, type EnterApiTransport } from '../http/api-module-deps';
import type { EnterApiConfig } from '../config';

type DomainFactory<TDomain> = (transport: EnterApiTransport) => TDomain;

interface EnterApiProviderRuntime {
    transport: EnterApiTransport;
    getDomain<TDomain>(factory: DomainFactory<TDomain>): TDomain;
}

const EnterApiTransportContext = createContext<EnterApiProviderRuntime | null>(null);
export const EnterApiConfigContext = createContext<EnterApiConfig | null>(null);

/** 为一个 Provider scope 创建按 factory 复用的领域实例，不静态引用任何具体领域。 */
export function createEnterApiProviderRuntime(
    transport: EnterApiTransport
): EnterApiProviderRuntime {
    const domains = new Map<DomainFactory<unknown>, unknown>();

    return {
        transport,
        getDomain<TDomain>(factory: DomainFactory<TDomain>): TDomain {
            if (!domains.has(factory as DomainFactory<unknown>)) {
                domains.set(factory as DomainFactory<unknown>, factory(transport));
            }
            return domains.get(factory as DomainFactory<unknown>) as TDomain;
        },
    };
}

/**
 * 在 React 根树中只创建一次传输依赖。
 * 领域客户端由独立 Hook 在真实消费点按需创建，不进入 Provider 的静态闭包。
 */
export function EnterApiProvider({
    children,
    config,
}: {
    children: ReactNode;
    config: EnterApiConfig;
}) {
    const runtime = useMemo(
        () => createEnterApiProviderRuntime(createEnterApiTransport(config)),
        [config]
    );

    return (
        <EnterApiConfigContext.Provider value={config}>
            <EnterApiTransportContext.Provider value={runtime}>
                {children}
            </EnterApiTransportContext.Provider>
        </EnterApiConfigContext.Provider>
    );
}

/** 读取 Provider 内唯一的传输依赖，供领域 Hook 组合客户端。 */
export function useEnterApiTransport(caller = 'useEnterApiTransport'): EnterApiTransport {
    const runtime = useContext(EnterApiTransportContext);
    if (!runtime) {
        throw new Error(`${caller} must be used within EnterApiProvider`);
    }
    return runtime.transport;
}

/** 按 Provider scope 复用领域实例，保住领域内部 WeakMap/batch/cache 的共享语义。 */
export function useEnterApiDomainInstance<TDomain>(
    factory: DomainFactory<TDomain>,
    caller = 'useEnterApiDomain'
): TDomain {
    const runtime = useContext(EnterApiTransportContext);
    if (!runtime) {
        throw new Error(`${caller} must be used within EnterApiProvider`);
    }
    return runtime.getDomain(factory);
}

/** 读取当前 Provider 使用的原始配置，供宿主集成层复用。 */
export function useEnterApiConfig(): EnterApiConfig {
    const config = useContext(EnterApiConfigContext);
    if (!config) {
        throw new Error('useEnterApiConfig must be used within EnterApiProvider');
    }
    return config;
}
